/**
 * PaymentSuccess.jsx — Page de confirmation de paiement + Facture
 *
 * Reçoit les données depuis navigate('/payment-success', { state: { reference, payment, booking, summary } }).
 * Permet d'imprimer/télécharger la facture via window.print() (CSS @media print dédié).
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiHome, FiLoader, FiMail, FiShield } from 'react-icons/fi';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import BottomNav from '../shared/BottomNav.jsx';
import './PaymentSuccess.css';

const API_BASE = getApiBaseUrl();

function formatCurrency(value) {
	return `${Number(value || 0).toLocaleString('fr-FR')} DH`;
}

function formatDate(dateStr) {
	if (!dateStr) return '—';
	try {
		return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
	} catch {
		return dateStr;
	}
}

function todayFormatted() {
	return new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function PaymentSuccess() {
	const location = useLocation();
	const navigate = useNavigate();
	const state = location.state || {};

	const { reference, payment, booking = {}, summary = {} } = state;

	// Gestion du retour Stripe après redirect 3D Secure
	// URL: /payment-success?payment_intent=pi_xxx&redirect_status=succeeded
	const [redirectPayment, setRedirectPayment] = useState(null);
	const [redirectLoading, setRedirectLoading] = useState(false);

	useEffect(() => {
		if (reference || payment) return; // déjà des données via navigate state
		const params = new URLSearchParams(location.search);
		const piId = params.get('payment_intent');
		const status = params.get('redirect_status');
		if (!piId || status !== 'succeeded') return;

		// Appelle le backend pour confirmer et créer le paiement
		setRedirectLoading(true);
		fetch(`${API_BASE}/api/payments/confirm`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ paymentIntentId: piId }),
		})
			.then(r => r.json())
			.then(data => {
				if (data?.reference) setRedirectPayment(data);
			})
			.catch(() => {})
			.finally(() => setRedirectLoading(false));
	}, [location.search, reference, payment]);

	// Cas redirect 3D Secure en cours
	if (redirectLoading) {
		return (
			<div className="pys-page">
				<main className="pys-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<div style={{ textAlign: 'center', color: '#6e7890' }}>
						<FiLoader size={32} style={{ animation: 'spin 1s linear infinite' }} />
						<p style={{ marginTop: '1rem' }}>Confirmation en cours…</p>
					</div>
				</main>
				<BottomNav />
			</div>
		);
	}

	// Données finales (état direct ou après redirect)
	const finalPayment = payment || redirectPayment;
	const finalReference = reference || redirectPayment?.reference;

	// Fallback si aucune donnée
	if (!finalReference && !finalPayment) {
		return (
			<div className="pys-page">
				<main className="pys-main">
					<div className="pys-empty">
						<p>Aucune information de paiement disponible.</p>
						<button type="button" className="pys-btn-home" onClick={() => navigate('/')}>
							Retour à l'accueil
						</button>
					</div>
				</main>
				<BottomNav />
			</div>
		);
	}

	const ref = finalReference || finalPayment?.reference || '—';
	const vehicleName = booking.vehicleName || finalPayment?.vehicleName || 'Véhicule';
	const customerEmail = booking.customerEmail || finalPayment?.customerEmail || '';
	const total = summary.total || finalPayment?.amount || 0;

	return (
		<div className="pys-page">
			<main className="pys-main">
				<div className="pys-hero">
					<div className="pys-check-wrap">
						<FiCheckCircle size={48} className="pys-check-icon" />
					</div>
					<h1>Paiement confirmé !</h1>
					<p className="pys-hero-sub">Votre réservation pour <strong>{vehicleName}</strong> est enregistrée.</p>
					<div className="pys-ref-badge">
						Référence : <strong>{ref}</strong>
					</div>
					<div className="pys-ref-badge" style={{ background: 'rgba(26,86,240,0.07)', borderColor: 'rgba(26,86,240,0.2)', marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
						<FiMail size={15} />
						Votre facture a été envoyée à <strong>{customerEmail || 'votre adresse email'}</strong>
					</div>
				</div>

				<div className="pys-actions-bar" style={{ justifyContent: 'center' }}>
					<button type="button" className="pys-btn-home" onClick={() => navigate('/')}>
						<FiHome size={16} /> Retour à l'accueil
					</button>
				</div>

				<p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#6e7890', marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
					<FiShield size={13} /> Paiement sécurisé par Stripe · {formatCurrency(total)} payés
				</p>
			</main>

			<BottomNav />
		</div>
	);
}

export default PaymentSuccess;
